import {
  DesignPriceBracketsDistributorPrices,
  PriceRange,
} from "../../../types";
import { useMemo } from "react";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { useTranslation } from "react-i18next";
import { FormattedPrice } from "./types";

/**
 * Hook to calculate prices for a design based on quantity and pricing brackets
 * @param distributorPrices - Pricing structure including brackets and credits
 * @param productName - Name of the product being priced
 * @param qty - Quantity being ordered
 */
export const useDistributorPriceCalculator = (
  distributorPrices: DesignPriceBracketsDistributorPrices | undefined,
  unitPriceBrackets: PriceRange[],
  qty: number,
  hasReachedMaximum: boolean,
  hasMinimum: boolean,
  shippingPrice: FormattedPrice
) => {
  const { t } = useTranslation();
  // Currency availability check
  const notAvailableInCurrency = useMemo(
    () => !distributorPrices,
    [distributorPrices]
  );

  const { FullCurrencyFormatter } = useCurrency();
  const distributorCost = useMemo(() => {
    if (notAvailableInCurrency) {
      return { price: 0, formatted: "" };
    }
    const price =
      distributorPrices?.distributorCost?.find((p) => qty < p.qty)?.price ?? 0;
    return { price, formatted: FullCurrencyFormatter?.format(price) };
  }, [
    distributorPrices?.distributorCost,
    FullCurrencyFormatter,
    notAvailableInCurrency,
    qty,
  ]);

  const distributorTotalPrice = useMemo(() => {
    if (notAvailableInCurrency) {
      return { price: 0, formatted: "" };
    }
    const price = distributorCost.price * qty + shippingPrice.price;
    return { price, formatted: FullCurrencyFormatter?.format(price) };
  }, [
    distributorCost.price,
    qty,
    FullCurrencyFormatter,
    notAvailableInCurrency,
    shippingPrice.price,
  ]);

  const distributorMsrp = useMemo(() => {
    if (notAvailableInCurrency) {
      return { price: 0, formatted: "" };
    }
    if (!hasMinimum) {
      return { price: 0, formatted: t("pricing.notMetMinimum") };
    }
    if (hasReachedMaximum) {
      return { price: 0, formatted: t("pricing.tailoredPrice") };
    }

    let unitPrice = 0;
    unitPriceBrackets?.some((p) => {
      if (qty < p.qty) {
        unitPrice = p.price;
        return true;
      }
      return false;
    });
    return {
      price: unitPrice,
      formatted: FullCurrencyFormatter?.format(unitPrice),
    };
  }, [
    notAvailableInCurrency,
    unitPriceBrackets,
    FullCurrencyFormatter,
    qty,
    hasReachedMaximum,
    hasMinimum,
    t,
  ]);

  return {
    distributorMsrp,
    distributorCost,
    distributorTotalPrice,
  };
};
